import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitCompareArrows, Loader2, Plus, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import type { LandingSectionVariantCompareRow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Props = {
  pageId: string;
  sectionId: string | null;
  sectionLabel?: string;
  days: number;
};

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

function pp(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  const x = Math.round(v * 1000) / 10;
  return `${x > 0 ? "+" : ""}${x} pp`;
}

function VariantCard({ row }: { row: LandingSectionVariantCompareRow }) {
  const [open, setOpen] = useState(false);
  const m = row.metrics || {};
  const d = row.delta_vs_previous;
  const improved =
    d &&
    ((typeof d.view_rate_pp === "number" && d.view_rate_pp > 0.005) ||
      (typeof d.cta_rate_of_viewers_pp === "number" && d.cta_rate_of_viewers_pp > 0.001));
  const worsened =
    d &&
    ((typeof d.view_rate_pp === "number" && d.view_rate_pp < -0.005) ||
      (typeof d.cta_rate_of_viewers_pp === "number" && d.cta_rate_of_viewers_pp < -0.001));

  return (
    <div
      className={cn(
        "rounded-lg border px-2.5 py-2 text-left",
        row.status === "active" ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold text-slate-900">{row.label}</div>
          <div className="text-[10px] text-slate-500">
            {row.created_at?.slice(0, 16).replace("T", " ")}
            {row.status === "active" ? " · aktywny" : " · zamknięty"}
          </div>
        </div>
        {d ? (
          <span
            className={cn(
              "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold",
              improved && "bg-emerald-100 text-emerald-800",
              worsened && "bg-rose-100 text-rose-800",
              !improved && !worsened && "bg-slate-100 text-slate-600",
            )}
          >
            {improved ? "↑" : worsened ? "↓" : "≈"} vs poprz.
          </span>
        ) : (
          <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
            baza
          </span>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-slate-600">
        <span>dojście {pct(m.view_rate)}</span>
        <span>CTA {pct(m.cta_rate_of_viewers)}</span>
        <span>{m.sessions_reached ?? 0} ses.</span>
      </div>
      {d ? (
        <div className="mt-1 text-[10px] text-slate-500">
          Δ dojście {pp(d.view_rate_pp)} · Δ CTA {pp(d.cta_rate_of_viewers_pp)}
        </div>
      ) : null}
      <button
        type="button"
        className="mt-1.5 text-[10px] font-medium text-slate-700 underline-offset-2 hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Ukryj treść" : "Podgląd treści"}
      </button>
      {open ? (
        <p className="mt-1 max-h-28 overflow-y-auto whitespace-pre-wrap text-[10px] leading-snug text-slate-600">
          {row.content_text_preview || "(brak tekstu)"}
        </p>
      ) : null}
    </div>
  );
}

function isSameWarsawDay(iso: string | undefined): boolean {
  if (!iso) return false;
  const day = iso.slice(0, 10);
  // Panel i serwer używają daty ISO z created_at (Warsaw); porównaj do lokalnego dnia operatora PL.
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return day === fmt.format(now);
}

export default function SectionVariantsPanel({
  pageId,
  sectionId,
  sectionLabel,
  days,
}: Props) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");

  const compareQ = useQuery({
    queryKey: ["landing-section-variants-compare", pageId, sectionId],
    queryFn: () => api.landingSectionVariantsCompare(pageId, sectionId!),
    enabled: Boolean(pageId && sectionId),
    retry: false,
  });

  const registerM = useMutation({
    mutationFn: () =>
      api.landingSectionVariantRegister({
        page_id: pageId,
        section_id: sectionId!,
        label: label.trim() || undefined,
        note: note.trim() || undefined,
        days,
      }),
    onSuccess: () => {
      setLabel("");
      setNote("");
      void qc.invalidateQueries({
        queryKey: ["landing-section-variants-compare", pageId, sectionId],
      });
    },
  });

  const refreshM = useMutation({
    mutationFn: () =>
      api.landingSectionVariantsRefreshMetrics({
        page_id: pageId,
        section_id: sectionId!,
        days,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["landing-section-variants-compare", pageId, sectionId],
      });
    },
  });

  const rows = useMemo(() => compareQ.data?.rows ?? [], [compareQ.data]);
  const hasSnapshotToday = useMemo(
    () => rows.some((r) => isSameWarsawDay(r.created_at)),
    [rows],
  );

  if (!sectionId) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
        Kliknij sekcję powyżej, żeby zobaczyć historię snapshotów.
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-slate-100 pt-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-900">
            <GitCompareArrows className="h-3.5 w-3.5 shrink-0 text-slate-500" />
            Porównywarka
          </div>
          <p className="truncate text-[10px] text-slate-500">
            {sectionLabel || sectionId} · #{sectionId}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          title="Odśwież metryki wariantów"
          disabled={refreshM.isPending || !rows.length}
          onClick={() => refreshM.mutate()}
        >
          {refreshM.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      <p className="rounded-md bg-slate-50 px-2 py-1.5 text-[10px] leading-snug text-slate-600">
        Po północy system sam zapisuje max 1 snapshot dziennie dla sekcji, których treść
        zmieniła się od ostatniego razu. Ręczny zapis też podlega temu limitowi.
      </p>

      {compareQ.data?.verdict_pl ? (
        <p className="rounded-md bg-slate-100 px-2 py-1.5 text-[11px] leading-snug text-slate-700">
          {compareQ.data.verdict_pl}
        </p>
      ) : null}

      <div className="space-y-1.5">
        {compareQ.isLoading && (
          <p className="py-2 text-[11px] text-slate-500">Wczytywanie wariantów…</p>
        )}
        {!compareQ.isLoading && rows.length === 0 && (
          <p className="py-2 text-[11px] text-slate-500">
            Brak snapshotów. Pierwszy zapisze się automatycznie po północy albo ręcznie poniżej.
          </p>
        )}
        {rows.map((row) => (
          <VariantCard key={row.id} row={row} />
        ))}
      </div>

      <div className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Snapshot dziś (opcjonalnie)
        </p>
        {hasSnapshotToday ? (
          <p className="text-[11px] text-slate-600">
            Dziś jest już snapshot tej sekcji. Kolejny możliwy po północy (tylko jeśli treść się
            zmieni).
          </p>
        ) : (
          <>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Etykieta (opcjonalnie)"
              className="h-8 text-xs"
            />
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notatka (opcjonalnie)"
              className="h-8 text-xs"
            />
            <Button
              type="button"
              size="sm"
              className="h-8 w-full text-xs"
              disabled={registerM.isPending}
              onClick={() => registerM.mutate()}
            >
              {registerM.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1.5 h-3.5 w-3.5" />
              )}
              Zapisz snapshot dziś
            </Button>
          </>
        )}
        {registerM.isError && (
          <p className="text-[10px] text-rose-600">
            {(registerM.error as Error)?.message || "Nie udało się zarejestrować"}
          </p>
        )}
      </div>
    </div>
  );
}
